/**
 * Find My Best Match — guided 5-question wizard for learners.
 *
 * Flow:
 *   Intro → 5 questions → Loading → Top 3 matched instructors → CTA
 *
 * Design principles:
 *   - Mobile-first, premium tone, one decision per screen.
 *   - No required fields beyond confidence + vehicle (postcode optional, broadens to nationwide).
 *   - Plain-English match reasons next to each instructor (why they fit).
 *   - Reuses existing trust signals (specialisms, tier, compliance, ratings).
 *
 * Backend: POST /api/marketplace/match-wizard
 */
import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Sparkles, Loader2, Check,
  PoundSterling, Star, BadgeCheck, Trophy, ShieldCheck, MapPin,
  Send, MessageCircle, Heart, RotateCcw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { trackFunnel } from "../utils/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────────
// Question definitions — single source of truth for the quiz UI
// ─────────────────────────────────────────────────────────────────────
const QUESTIONS = [
  {
    id: "confidence",
    title: "How are you feeling about driving?",
    sub: "Be honest — there's no wrong answer.",
    options: [
      { value: "nervous", label: "Nervous", desc: "I'd like a calm, patient instructor." },
      { value: "building", label: "Building confidence", desc: "I've had a few lessons or a break." },
      { value: "confident", label: "Confident", desc: "I want to push for test-ready quickly." },
    ],
  },
  {
    id: "vehicle",
    title: "Manual or automatic?",
    sub: "Manual gives you a full licence; automatic is simpler to learn.",
    options: [
      { value: "manual", label: "Manual", desc: "Full UK licence." },
      { value: "automatic", label: "Automatic", desc: "Easier to learn, automatic-only licence." },
      { value: "unsure", label: "Not sure yet", desc: "Show me both options." },
    ],
  },
  {
    id: "style",
    title: "What teaching style works best for you?",
    sub: "Pick the one that feels most like you.",
    options: [
      { value: "patient", label: "Patient & gentle", desc: "Step-by-step, no pressure." },
      { value: "structured", label: "Structured", desc: "Clear plan toward the test." },
      { value: "fast_track", label: "Fast-track", desc: "Intensive course, get on the road quickly." },
      { value: "calm", label: "Calm & reassuring", desc: "Anxiety-aware, kind tone." },
    ],
  },
  {
    id: "extras",
    title: "Any extra support you'd value?",
    sub: "Optional — pick any that apply, or skip.",
    multi: true,
    options: [
      { value: "neurodivergent", label: "Neurodivergent-friendly" },
      { value: "anxiety", label: "Anxiety-aware" },
      { value: "female_instructor", label: "Female instructor" },
      { value: "male_instructor", label: "Male instructor" },
      { value: "lgbtq", label: "LGBTQ+ friendly" },
      { value: "disability", label: "Disability confident" },
      { value: "motorway", label: "Motorway lessons" },
      { value: "night", label: "Night driving" },
      { value: "pass_plus", label: "Pass Plus" },
      { value: "ev", label: "EV / Hybrid" },
      { value: "towing", label: "Towing / trailer" },
      { value: "intensive", label: "Intensive course" },
    ],
  },
  {
    id: "location",
    title: "Where should we look?",
    sub: "Postcode helps us find instructors near you. Both are optional.",
    custom: "location",
  },
];

const TOTAL_STEPS = QUESTIONS.length;

// ─────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────
export default function MatchWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(-1); // -1 = intro, 0..n = questions, n+1 = results
  const [answers, setAnswers] = useState({
    confidence: "",
    vehicle: "",
    style: "",
    extras: [],
    postcode: "",
    preferred_times: "",
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  // Funnel: page viewed (intro screen mounted)
  useEffect(() => { trackFunnel("best_match", "viewed"); }, []);

  const progressPct = useMemo(() => {
    if (step < 0) return 0;
    if (step >= TOTAL_STEPS) return 100;
    return Math.round(((step + 1) / TOTAL_STEPS) * 100);
  }, [step]);

  const setAnswer = (id, value) => {
    setAnswers((a) => ({ ...a, [id]: value }));
  };

  const toggleExtra = (value) => {
    setAnswers((a) => {
      const cur = a.extras || [];
      return { ...a, extras: cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value] };
    });
  };

  const canAdvance = (q) => {
    if (q.multi) return true; // extras optional
    if (q.custom === "location") return true; // postcode optional
    return Boolean(answers[q.id]);
  };

  const next = () => {
    if (step + 1 < TOTAL_STEPS) {
      setStep(step + 1);
    } else {
      submit();
    }
  };

  const back = () => {
    if (step > -1) setStep(step - 1);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/marketplace/match-wizard`, answers);
      setResults(data);
      setStep(TOTAL_STEPS); // results screen
      trackFunnel("best_match", "completed", {
        match_count: (data?.matches || []).length,
        confidence: answers.confidence,
        vehicle: answers.vehicle,
      });
    } catch (e) {
      toast.error("Couldn't find matches right now. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setAnswers({ confidence: "", vehicle: "", style: "", extras: [], postcode: "", preferred_times: "" });
    setResults(null);
    setStep(-1);
  };

  // ── INTRO ───────────────────────────────────────────────────────────
  if (step === -1) {
    return (
      <div className="min-h-dvh no-overflow-x bg-gradient-to-b from-slate-950 via-slate-900 to-indigo-950 text-white flex flex-col">
        <div className="px-4 py-3 flex items-center">
          <button
            data-testid="match-wizard-close"
            onClick={() => nav("/marketplace")}
            className="opacity-70 hover:opacity-100 inline-flex items-center text-sm"
          >
            <ChevronLeft className="w-4 h-4" /> Marketplace
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20 mb-6">
            <Sparkles className="w-8 h-8 text-slate-900" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3" data-testid="match-wizard-title">
            Find your best instructor
          </h1>
          <p className="text-base text-slate-300 max-w-md mb-1">
            Five quick questions. We'll match you with the three instructors who fit you best.
          </p>
          <p className="text-xs text-slate-400 max-w-md mb-10">
            Powered by verified specialisms, professional standards, and trusted compliance.
          </p>
          <Button
            data-testid="match-wizard-start"
            onClick={() => { trackFunnel("best_match", "started"); setStep(0); }}
            className="h-14 px-8 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 font-bold text-base hover:from-amber-300 hover:to-amber-400 shadow-xl shadow-amber-500/20"
          >
            Start — takes 60 seconds <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
          <div className="mt-12 grid grid-cols-3 gap-3 w-full max-w-md">
            <Pill icon={Trophy} label="Verified standards" />
            <Pill icon={ShieldCheck} label="Compliant pros" />
            <Pill icon={Heart} label="Right-fit match" />
          </div>
        </div>
      </div>
    );
  }

  // ── RESULTS ─────────────────────────────────────────────────────────
  if (step === TOTAL_STEPS && results) {
    return <ResultsView results={results} onRestart={restart} onClose={() => nav("/marketplace")} />;
  }

  // ── QUESTIONS ───────────────────────────────────────────────────────
  const q = QUESTIONS[step];
  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex flex-col">
      {/* Progress bar */}
      <div className="sticky top-0 z-10 bg-slate-50/95 dark:bg-[#0c1120]/95 backdrop-blur border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="match-wizard-back"
            onClick={back}
            className="text-slate-500 hover:text-slate-900 dark:hover:text-white"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
            <div
              data-testid="match-wizard-progress"
              className="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 tabular-nums">
            {step + 1}/{TOTAL_STEPS}
          </span>
        </div>
      </div>

      <div className="flex-1 max-w-xl w-full mx-auto px-5 py-6 sm:py-10">
        <h2
          data-testid={`match-wizard-q-${q.id}-title`}
          className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-2"
        >
          {q.title}
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">{q.sub}</p>

        {/* Question body */}
        {q.custom === "location" ? (
          <LocationStep
            postcode={answers.postcode}
            preferred_times={answers.preferred_times}
            onChange={(k, v) => setAnswer(k, v)}
          />
        ) : q.multi ? (
          <div className="grid grid-cols-2 gap-2.5">
            {q.options.map((opt) => {
              const on = (answers.extras || []).includes(opt.value);
              return (
                <button
                  key={opt.value}
                  data-testid={`match-wizard-extra-${opt.value}`}
                  data-state={on ? "on" : "off"}
                  onClick={() => toggleExtra(opt.value)}
                  className={`text-left rounded-xl border p-3 transition active:scale-[0.98] ${
                    on
                      ? "bg-amber-50 dark:bg-amber-500/10 border-amber-400 dark:border-amber-500/50"
                      : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div
                      className={`mt-0.5 w-4 h-4 rounded-md flex items-center justify-center flex-shrink-0 ${
                        on ? "bg-amber-500 text-slate-950" : "border border-slate-300 dark:border-slate-600"
                      }`}
                    >
                      {on && <Check className="w-3 h-3" />}
                    </div>
                    <span className="text-sm font-semibold text-slate-900 dark:text-white">{opt.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2.5">
            {q.options.map((opt) => {
              const on = answers[q.id] === opt.value;
              return (
                <button
                  key={opt.value}
                  data-testid={`match-wizard-${q.id}-${opt.value}`}
                  data-state={on ? "on" : "off"}
                  onClick={() => {
                    setAnswer(q.id, opt.value);
                    // Auto-advance for single-select after a brief tap-feedback delay
                    setTimeout(() => {
                      if (step + 1 < TOTAL_STEPS) setStep((s) => s + 1);
                    }, 150);
                  }}
                  className={`w-full text-left rounded-2xl border p-4 transition active:scale-[0.99] ${
                    on
                      ? "bg-amber-50 dark:bg-amber-500/10 border-amber-400 dark:border-amber-500/60 shadow-sm"
                      : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-600"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        on ? "bg-amber-500 text-slate-950" : "border-2 border-slate-300 dark:border-slate-600"
                      }`}
                    >
                      {on && <Check className="w-3 h-3" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-bold text-slate-900 dark:text-white">{opt.label}</div>
                      {opt.desc && (
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{opt.desc}</div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Forward button (only required for multi-select / location) */}
        {(q.multi || q.custom === "location") && (
          <div className="mt-8">
            <Button
              data-testid="match-wizard-next"
              onClick={next}
              disabled={loading || !canAdvance(q)}
              className="w-full h-14 rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900 text-white font-bold text-base"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : step + 1 === TOTAL_STEPS ? (
                <>Find my matches <Sparkles className="w-4 h-4 ml-2" /></>
              ) : (
                <>Next <ChevronRight className="w-5 h-5 ml-1" /></>
              )}
            </Button>
            {q.multi && (answers.extras || []).length === 0 && (
              <button
                data-testid="match-wizard-skip"
                onClick={next}
                className="mt-3 w-full text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              >
                Skip — show me everyone
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────

function Pill({ icon: Icon, label }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl px-2 py-2.5 flex flex-col items-center gap-1 text-center">
      <Icon className="w-4 h-4 text-amber-400" />
      <span className="text-[10px] font-semibold text-slate-300">{label}</span>
    </div>
  );
}

function LocationStep({ postcode, preferred_times, onChange }) {
  return (
    <div className="space-y-5">
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2 block">
          Postcode <span className="text-slate-400 normal-case">(optional)</span>
        </label>
        <div className="relative">
          <MapPin className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="match-wizard-postcode"
            value={postcode}
            onChange={(e) => onChange("postcode", e.target.value.toUpperCase())}
            placeholder="e.g. SW1A 1AA"
            className="pl-10 h-12 bg-white dark:bg-slate-900"
          />
        </div>
        <p className="text-[11px] text-slate-500 dark:text-slate-500 mt-2">
          We'll show you instructors in your area first. Leave blank to see verified instructors UK-wide.
        </p>
      </div>
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2 block">
          Preferred times <span className="text-slate-400 normal-case">(optional)</span>
        </label>
        <Input
          data-testid="match-wizard-times"
          value={preferred_times}
          onChange={(e) => onChange("preferred_times", e.target.value)}
          placeholder="e.g. weekday evenings, Saturday mornings"
          className="h-12 bg-white dark:bg-slate-900"
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Results
// ─────────────────────────────────────────────────────────────────────
function ResultsView({ results, onRestart, onClose }) {
  const nav = useNavigate();
  const matches = results?.matches || [];
  const quizAnswers = results?.answers || {};
  const level = results?.match_level;
  const levelLabel =
    level === "exact_outward" ? "in your area"
    : level === "same_area" ? "near you"
    : level === "nationwide" ? "across the UK"
    : "";

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-16">
      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white px-5 pt-6 pb-10">
        <div className="max-w-xl mx-auto">
          <button
            data-testid="match-wizard-results-close"
            onClick={onClose}
            className="text-sm opacity-70 hover:opacity-100 inline-flex items-center mb-5"
          >
            <ChevronLeft className="w-4 h-4" /> Marketplace
          </button>
          <div className="inline-flex items-center gap-1.5 bg-amber-500/20 border border-amber-400/40 rounded-full px-3 py-1 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[11px] font-bold tracking-wider uppercase text-amber-200">Your top matches</span>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight mb-2" data-testid="match-wizard-results-title">
            {matches.length === 0
              ? "No matches yet"
              : `${matches.length} instructor${matches.length === 1 ? "" : "s"} for you ${levelLabel}`.trim()}
          </h1>
          <p className="text-sm text-slate-300">
            {matches.length === 0
              ? "We don't have a verified instructor that fits all your needs in this area yet. Try broadening your search or checking back soon."
              : "Ranked by how well their specialisms match your answers, then by professional standards and compliance."}
          </p>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 -mt-6">
        {matches.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-8 text-center shadow-sm">
            <p className="text-sm font-bold text-slate-900 dark:text-white mb-1">Nothing matched right now.</p>
            <p className="text-xs text-slate-500 mb-5">Try broadening your area or different preferences.</p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button data-testid="match-wizard-results-retry" onClick={onRestart} variant="outline" className="rounded-full">
                <RotateCcw className="w-4 h-4 mr-1" /> Start over
              </Button>
              <Button data-testid="match-wizard-results-browse" onClick={() => nav("/marketplace")} className="rounded-full bg-slate-900 dark:bg-white dark:text-slate-900 text-white">
                Browse all instructors
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {matches.map((m, idx) => (
              <MatchCard key={m.user_id} m={m} rank={idx + 1} quizAnswers={quizAnswers} />
            ))}
            <div className="flex flex-col sm:flex-row gap-2 pt-3">
              <Button data-testid="match-wizard-restart" onClick={onRestart} variant="outline" className="rounded-full flex-1">
                <RotateCcw className="w-4 h-4 mr-1" /> Re-take quiz
              </Button>
              <Button data-testid="match-wizard-browse-all" onClick={() => nav("/marketplace")} className="rounded-full flex-1 bg-slate-900 dark:bg-white dark:text-slate-900 text-white">
                Browse all instructors
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TierChip({ tier }) {
  if (!tier || !["black", "platinum", "gold"].includes(tier)) return null;
  const map = {
    black: { label: "Master", classes: "bg-zinc-900 text-amber-300 border border-amber-300/40" },
    platinum: { label: "Elite", classes: "bg-cyan-50 text-cyan-700 border border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-700/50" },
    gold: { label: "Pro", classes: "bg-amber-50 text-amber-800 border border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700/50" },
  };
  const t = map[tier];
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${t.classes}`}>
      <Trophy className="w-2.5 h-2.5" /> {t.label}
    </span>
  );
}

function MatchCard({ m, rank, quizAnswers = {} }) {
  const nav = useNavigate();
  const initial = (m.name || m.business_name || "I").trim().charAt(0).toUpperCase();
  const reasons = m.match?.reasons || [];
  const hours = m.availability?.hours || {};
  const hoursLine =
    hours.start && hours.end && Array.isArray(hours.days) && hours.days.length
      ? `${hours.days.join(", ")} · ${hours.start}–${hours.end}`
      : null;

  return (
    <div
      data-testid={`match-card-${m.user_id}`}
      className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden"
    >
      {/* Rank ribbon */}
      <div className="bg-gradient-to-r from-amber-50 to-transparent dark:from-amber-500/10 dark:to-transparent px-4 py-2 flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/70">
        <div className="inline-flex items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-wider text-amber-700 dark:text-amber-400">
            #{rank} match
          </span>
          {m.match?.hits > 0 && (
            <span data-testid={`match-card-hits-${m.user_id}`} className="text-[10px] font-bold text-slate-500 dark:text-slate-400">
              · {m.match.hits} reason{m.match.hits === 1 ? "" : "s"} you'll click
            </span>
          )}
        </div>
        <TierChip tier={m.standards?.tier} />
      </div>

      <div className="p-4">
        {/* Header */}
        <div className="flex gap-3 items-start">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-2xl font-extrabold flex-shrink-0">
            {m.photo_url ? (
              <img src={m.photo_url} alt={m.name} className="w-full h-full rounded-2xl object-cover" />
            ) : initial}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-bold text-slate-900 dark:text-white truncate">
                {m.business_name || m.name}
              </span>
              <BadgeCheck className="w-4 h-4 text-blue-500" />
              {m.standards?.is_compliant && (
                <span data-testid={`match-card-compliant-${m.user_id}`} className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-700/50">
                  <ShieldCheck className="w-2.5 h-2.5" /> Compliant
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2 mt-0.5">
              {m.postcode_outward && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {m.postcode_outward}
                </span>
              )}
              {typeof m.hourly_rate === "number" && (
                <>
                  <span className="text-slate-300 dark:text-slate-600">·</span>
                  <span className="inline-flex items-center font-semibold text-slate-700 dark:text-slate-200">
                    <PoundSterling className="w-3 h-3" />{m.hourly_rate}/hr
                  </span>
                </>
              )}
              {m.rating_snapshot && (
                <>
                  <span className="text-slate-300 dark:text-slate-600">·</span>
                  <span className="inline-flex items-center gap-1">
                    <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                    <span className="font-semibold">{m.rating_snapshot.avg_rating.toFixed(1)}</span>
                    <span className="text-slate-400">({m.rating_snapshot.response_count})</span>
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Why this match — plain English */}
        {reasons.length > 0 && (
          <div className="mt-3 bg-amber-50/70 dark:bg-amber-500/5 border border-amber-200/70 dark:border-amber-500/20 rounded-xl px-3 py-2.5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300 mb-1.5 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Why they fit you
            </div>
            <ul className="space-y-1">
              {reasons.map((r, i) => (
                <li
                  key={i}
                  data-testid={`match-card-reason-${m.user_id}-${i}`}
                  className="text-[13px] text-slate-800 dark:text-slate-100 flex items-start gap-1.5"
                >
                  <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Availability snapshot */}
        <div className="mt-3 flex items-center gap-2 text-xs">
          <span
            data-testid={`match-card-availability-${m.user_id}`}
            className={`inline-flex items-center gap-1 font-semibold px-2 py-0.5 rounded-full ${
              m.availability?.accepting_new_pupils
                ? "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30"
                : "text-slate-500 bg-slate-100 dark:bg-slate-800"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${m.availability?.accepting_new_pupils ? "bg-emerald-500" : "bg-slate-400"}`} />
            {m.availability?.accepting_new_pupils ? "Accepting new pupils" : "Not currently accepting"}
          </span>
          {hoursLine && <span className="text-slate-500 dark:text-slate-400 truncate">{hoursLine}</span>}
        </div>

        {/* CTAs */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <Button
            data-testid={`match-card-view-${m.user_id}`}
            onClick={() => {
              trackFunnel("best_match", "result_clicked", { match_user_id: m.user_id, rank, action: "view_profile" });
              nav(`/marketplace/instructor/${m.user_id}`, { state: { fromQuiz: true, answers: quizAnswers, instructorName: m.name } });
            }}
            variant="outline"
            className="rounded-xl text-xs font-semibold h-10"
          >
            View profile
          </Button>
          <Button
            data-testid={`match-card-message-${m.user_id}`}
            onClick={() => {
              trackFunnel("best_match", "messaged_match", { match_user_id: m.user_id, rank });
              nav(`/marketplace/instructor/${m.user_id}`, {
                state: {
                  fromQuiz: true,
                  prefillFromQuiz: true,
                  answers: quizAnswers,
                  instructorName: m.name,
                  scrollToContact: true,
                }
              });
            }}
            className="rounded-xl text-xs font-bold h-10 bg-slate-900 dark:bg-white dark:text-slate-900 text-white col-span-2"
          >
            <Send className="w-3.5 h-3.5 mr-1" /> Message
          </Button>
        </div>
      </div>
    </div>
  );
}
